import { useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import {
  AssessmentType,
  GetAssessmentDocument,
  GetAssessmentQuery,
  useGetAssessmentQuery,
} from '@gql/gql-types';
import CodeReviewMain from '@pages/code-review/code-review-main';
import LiveCodingMain from '@pages/live-coding/live-coding-main';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { usePerformanceMonitor } from '../../hooks/use-performance-monitor';
import { useMediaDevicesContext } from '../../providers/media-devices-context';
import {
  InterviewComplete,
  InterviewDeviceAccessError,
  InterviewErrorLoading,
  InterviewLoading,
  InterviewNotFound,
} from './components/interview-state-views';
import InterviewMain from './interview-main';
import InterviewSetup from './interview-setup';

const PARENT_RESPONSE_TIMEOUT = 5000; // 5 seconds

type InterviewPageProps = {
  accessToken: string;
};

const InterviewPage = ({ accessToken }: InterviewPageProps) => {
  usePerformanceMonitor();
  const { id: interviewId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    isAudioEnabled,
    isVideoEnabled,
    toggleAudio,
    toggleVideo,
    checkMediaDevicePermissions,
  } = useMediaDevicesContext();

  // Read (obfuscated) requestedProvider from URL query parameters
  const requestedProviderParam = searchParams.get('requestedProvider');
  const requestedProvider =
    requestedProviderParam === 'aurora' ||
    requestedProviderParam === 'polaris' ||
    requestedProviderParam === 'zenith'
      ? requestedProviderParam
      : undefined;

  const client = useApolloClient();

  const {
    loading: isAssessmentLoading,
    data: assessmentData,
    error: assessmentError,
  } = useGetAssessmentQuery({
    variables: { id: interviewId || 'missing-interview-id' },
  });

  const [showDeviceSetup, setShowDeviceSetup] = useState(true);
  const [showMediaDeviceError, setShowMediaDeviceError] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isWaitingForStart, setIsWaitingForStart] = useState(false);
  const [canStartInterview, setCanStartInterview] = useState(false);

  useEffect(() => {
    if (interviewId) {
      datadogRum.setGlobalContextProperty('interviewId', interviewId);
    }
    return () => {
      datadogRum.removeGlobalContextProperty('interviewId');
    };
  }, [interviewId]);

  useEffect(() => {
    const templateType = assessmentData?.assessment?.template?.templateType;
    datadogRum.setGlobalContextProperty(
      'assessmentType',
      templateType || 'unknown',
    );
    return () => {
      datadogRum.removeGlobalContextProperty('assessmentType');
    };
  }, [assessmentData?.assessment?.template?.templateType]);

  useEffect(() => {
    if (assessmentData?.assessment?.completedAt) {
      setIsCompleted(true);
    }
  }, [assessmentData]);

  // Clean up media streams and notify parent when interview ends
  useEffect(() => {
    if (isCompleted) {
      if (isVideoEnabled) {
        toggleVideo();
      }
      if (isAudioEnabled) {
        toggleAudio();
      }
    }
  }, [isCompleted, isAudioEnabled, isVideoEnabled, toggleVideo, toggleAudio]);

  // Poll media device access to check if the user has revoked permissions
  useEffect(() => {
    if (isAssessmentLoading || showDeviceSetup || isCompleted) {
      return;
    }

    let pollMediaDeviceAccess: NodeJS.Timeout | undefined = setInterval(
      async () => {
        try {
          const granted = await checkMediaDevicePermissions();
          if (!granted) {
            setShowMediaDeviceError(true);
            if (pollMediaDeviceAccess) {
              clearInterval(pollMediaDeviceAccess);
              pollMediaDeviceAccess = undefined;
            }
          }
        } catch (err) {
          console.warn(`Error checking media device permissions: ${err}`);
        }
      },
      1000,
    );

    return () => {
      if (pollMediaDeviceAccess) {
        clearInterval(pollMediaDeviceAccess);
        pollMediaDeviceAccess = undefined;
      }
    };
  }, [
    isAssessmentLoading,
    showDeviceSetup,
    isCompleted,
    checkMediaDevicePermissions,
  ]);

  const updateAssessmentCache = useCallback(
    (startedAt: Date) => {
      const currentData = client.readQuery<GetAssessmentQuery>({
        query: GetAssessmentDocument,
        variables: { id: interviewId },
      });

      if (currentData?.assessment) {
        client.writeQuery({
          query: GetAssessmentDocument,
          variables: { id: interviewId },
          data: {
            assessment: {
              ...currentData.assessment,
              startedAt: startedAt.toISOString(),
            },
          },
        });
      }
    },
    [client, interviewId],
  );

  useEffect(() => {
    // Only set up message listener if we're in an iframe
    const isIframe = window.self !== window.top;
    if (isIframe) {
      const handleParentMessage = (event: MessageEvent) => {
        if (event.data === 'INTERVIEW_CAN_START_TRUE') {
          // need to add a timeout here to still be able to start the interview in between deployments
          // remove this after deployment to prod
          if (window.interviewCanStartTimeoutId) {
            clearTimeout(window.interviewCanStartTimeoutId);
            delete window.interviewCanStartTimeoutId;
          }
          setCanStartInterview(true);
          setShowDeviceSetup(false);
        } else if (event.data === 'INTERVIEW_CAN_START_FALSE') {
          if (window.interviewCanStartTimeoutId) {
            clearTimeout(window.interviewCanStartTimeoutId);
            delete window.interviewCanStartTimeoutId;
          }
          setIsWaitingForStart(false);
        }
      };

      window.addEventListener('message', handleParentMessage);
      return () => {
        window.removeEventListener('message', handleParentMessage);
        if (window.interviewCanStartTimeoutId) {
          clearTimeout(window.interviewCanStartTimeoutId);
          delete window.interviewCanStartTimeoutId;
        }
      };
    }
  }, []);

  const onRequestStart = useCallback(() => {
    setIsWaitingForStart(true);
    const isIframe = window.self !== window.top;
    if (isIframe) {
      window.parent.postMessage('INTERVIEW_CAN_START', '*');

      // Set timeout to auto-start if no response
      const timeoutId = setTimeout(() => {
        console.log(
          'No response from parent window after 5s, auto-starting interview',
        );
        setCanStartInterview(true);
        setShowDeviceSetup(false);
      }, PARENT_RESPONSE_TIMEOUT);

      window.interviewCanStartTimeoutId = timeoutId;
    } else {
      setCanStartInterview(true);
      setShowDeviceSetup(false);
    }
  }, []);

  const onStarted = useCallback(() => {
    // Notify the parent window that the interview has started.
    if (window.self !== window.top) {
      window.parent.postMessage('INTERVIEW_STARTED', '*');
    }
  }, []);

  const onCompleted = useCallback(() => {
    setIsCompleted(true);
  }, []);

  useEffect(() => {
    if (isCompleted) {
      // Notify the parent window that the interview is complete.
      if (window.self !== window.top) {
        window.parent.postMessage('INTERVIEW_COMPLETE', '*');
      }
    }
  }, [isCompleted]);

  if (!interviewId || assessmentError) {
    return <InterviewErrorLoading />;
  }

  if (isAssessmentLoading) {
    return <InterviewLoading />;
  }

  if (!assessmentData?.assessment || !assessmentData.assessment.template) {
    return <InterviewNotFound />;
  }

  if (showMediaDeviceError) {
    return <InterviewDeviceAccessError />;
  }

  if (isCompleted) {
    return <InterviewComplete assessmentId={interviewId} />;
  }

  const isScreenSharingRequired =
    assessmentData.assessment.template.templateType ===
      AssessmentType.LiveCoding ||
    assessmentData.assessment.template.templateType ===
      AssessmentType.CodeReview;

  if (showDeviceSetup) {
    return (
      <InterviewSetup
        isStarted={!!assessmentData.assessment.startedAt}
        duration={assessmentData.assessment.template.duration ?? 0}
        onRequestStart={onRequestStart}
        isWaitingForStart={isWaitingForStart}
        isLiveCoding={isScreenSharingRequired}
      />
    );
  }

  if (!canStartInterview) {
    return <InterviewLoading />;
  }

  if (
    assessmentData.assessment.template.templateType ===
    AssessmentType.LiveCoding
  ) {
    return (
      <LiveCodingMain
        interviewId={interviewId}
        accessToken={accessToken}
        assessment={assessmentData.assessment}
        requestedProvider={requestedProvider}
        onStarted={onStarted}
        onCompleted={onCompleted}
        updateAssessmentCache={updateAssessmentCache}
      />
    );
  }

  if (
    assessmentData.assessment.template.templateType ===
    AssessmentType.CodeReview
  ) {
    return (
      <CodeReviewMain
        interviewId={interviewId}
        accessToken={accessToken}
        assessment={assessmentData.assessment}
        requestedProvider={requestedProvider}
        onStarted={onStarted}
        onCompleted={onCompleted}
        updateAssessmentCache={updateAssessmentCache}
      />
    );
  }

  return (
    <InterviewMain
      interviewId={interviewId}
      accessToken={accessToken}
      requestedProvider={requestedProvider}
      onStarted={onStarted}
      onCompleted={onCompleted}
      updateAssessmentCache={updateAssessmentCache}
    />
  );
};

export default InterviewPage;
